import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Settings, Crown, LogOut, User, ChevronDown, Gift, Globe, ChevronRight, Check, Star, Bell, Lock, MessageSquare, HelpCircle, AlertCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTour, TOUR_IDS } from '../contexts/TourContext';
import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import AccountSettingsModal from './AccountSettingsModal';
import ReferralModal from './ReferralModal';
import UpgradeModal from './UpgradeModal';
import NotificationsDropdown, { NotificationBell } from './NotificationsDropdown';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_b7533cee-6cc1-4a41-9e88-4b20184fec89/artifacts/eixt3fs2_STM%20mobile%20logo.png';

const LANGUAGES = [
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'tr', name: 'Türkçe', flag: '🇹🇷' }
];

const Navigation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, token, isAuthenticated, isPremium, isPro, logout, refreshUser } = useAuth();
  const { startTour } = useTour();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showLanguageMenu, setShowLanguageMenu] = useState(false);
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [showReferralModal, setShowReferralModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeFeature, setUpgradeFeature] = useState('');
  const [showNotifications, setShowNotifications] = useState(false);
  
  // Refs for collision-aware positioning
  const menuTriggerRef = useRef(null);
  const menuRef = useRef(null);
  const languageTriggerRef = useRef(null);
  const languageMenuRef = useRef(null);
  
  // Position states
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [languageMenuPosition, setLanguageMenuPosition] = useState({ top: 0, left: 0 });
  const [languageMenuSide, setLanguageMenuSide] = useState('right');

  const isActive = (path) => location.pathname === path;

  // Calculate dropdown position with collision detection
  const updateMenuPosition = useCallback(() => {
    if (!menuTriggerRef.current || !showUserMenu) return;
    
    const triggerRect = menuTriggerRef.current.getBoundingClientRect();
    const menuWidth = 224; // w-56 = 14rem = 224px
    const menuHeight = 300; // approximate
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const padding = 8;
    
    let top = triggerRect.bottom + 8;
    let left = triggerRect.right - menuWidth;
    
    // Horizontal bounds
    if (left < padding) {
      left = padding;
    } else if (left + menuWidth > viewportWidth - padding) {
      left = viewportWidth - menuWidth - padding;
    }
    
    // Vertical bounds - flip up if needed
    if (top + menuHeight > viewportHeight - padding) {
      top = Math.max(padding, triggerRect.top - menuHeight - 8);
    }
    
    setMenuPosition({ top, left });
  }, [showUserMenu]);

  // Calculate language submenu position
  const updateLanguageMenuPosition = useCallback(() => {
    if (!languageTriggerRef.current || !showLanguageMenu) return;
    
    const triggerRect = languageTriggerRef.current.getBoundingClientRect();
    const submenuWidth = 176; // w-44 = 11rem = 176px
    const submenuHeight = 160; // approximate
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const padding = 8;
    const gap = 4;
    
    let top = triggerRect.top;
    let left = 0;
    let side = 'right';
    
    // Check space on right vs left
    const spaceRight = viewportWidth - triggerRect.right - gap;
    const spaceLeft = triggerRect.left - gap;
    
    if (spaceRight >= submenuWidth) {
      left = triggerRect.right + gap;
      side = 'right';
    } else if (spaceLeft >= submenuWidth) {
      left = triggerRect.left - submenuWidth - gap;
      side = 'left';
    } else {
      // Not enough space on either side, overlay on trigger
      left = Math.max(padding, viewportWidth - submenuWidth - padding);
      side = 'right';
    }
    
    // Vertical bounds
    if (top + submenuHeight > viewportHeight - padding) {
      top = viewportHeight - submenuHeight - padding;
    }
    top = Math.max(padding, top);
    
    setLanguageMenuPosition({ top, left });
    setLanguageMenuSide(side);
  }, [showLanguageMenu]);

  // Update positions on scroll/resize
  useEffect(() => {
    if (showUserMenu) {
      updateMenuPosition();
      window.addEventListener('scroll', updateMenuPosition, true);
      window.addEventListener('resize', updateMenuPosition);
      return () => {
        window.removeEventListener('scroll', updateMenuPosition, true);
        window.removeEventListener('resize', updateMenuPosition);
      };
    }
  }, [showUserMenu, updateMenuPosition]);

  useEffect(() => {
    if (showLanguageMenu) {
      updateLanguageMenuPosition();
      window.addEventListener('scroll', updateLanguageMenuPosition, true);
      window.addEventListener('resize', updateLanguageMenuPosition);
      return () => {
        window.removeEventListener('scroll', updateLanguageMenuPosition, true);
        window.removeEventListener('resize', updateLanguageMenuPosition);
      };
    }
  }, [showLanguageMenu, updateLanguageMenuPosition]);

  // Close menus on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      const clickedInsideMenu = menuRef.current?.contains(event.target);
      const clickedInsideTrigger = menuTriggerRef.current?.contains(event.target);
      const clickedInsideLanguageMenu = languageMenuRef.current?.contains(event.target);
      const clickedInsideLanguageTrigger = languageTriggerRef.current?.contains(event.target);
      
      if (!clickedInsideMenu && !clickedInsideTrigger && !clickedInsideLanguageMenu && !clickedInsideLanguageTrigger) {
        setShowUserMenu(false);
        setShowLanguageMenu(false);
      }
    };
    
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setShowUserMenu(false);
        setShowLanguageMenu(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  const handleLogout = () => {
    logout();
    setShowUserMenu(false);
    navigate('/login');
  };

  const handleLanguageSelect = async (langCode) => {
    try {
      await axios.put(`${API_BASE}/api/auth/language`, 
        { language: langCode },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await refreshUser();
    } catch (error) {
      console.error('Failed to update language:', error);
    }
    setShowLanguageMenu(false);
    setShowUserMenu(false);
  };

  const getUserInitial = () => {
    if (user?.email) {
      return user.email[0].toUpperCase();
    }
    return 'U';
  };

  const getCurrentLanguage = () => {
    return LANGUAGES.find(l => l.code === (user?.language || 'en')) || LANGUAGES[0];
  };

  return (
    <>
      <nav className="border-b hidden md:block" style={{ backgroundColor: '#0B0F1A', borderColor: 'rgba(255,255,255,0.06)' }}>
        <div className="max-w-[1600px] mx-auto px-6">
          <div className="flex items-center justify-between h-16">
            <Link to="/app" className="flex items-center gap-2" data-testid="nav-logo">
              <img 
                src={LOGO_URL} 
                alt="ScopeTheMarket" 
                className="h-9 w-auto"
              />
              <span 
                className="text-lg font-semibold"
                style={{ 
                  fontFamily: 'Inter, sans-serif',
                  background: 'linear-gradient(90deg, #00E5FF 0%, #3B82F6 50%, #8B5CF6 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                ScopeTheMarket
              </span>
            </Link>

            <div className="flex items-center gap-1">
              <Link
                to="/app"
                className="px-4 py-2 text-sm font-medium transition-colors rounded-md"
                style={{
                  color: isActive('/app') || isActive('/app/') ? '#F8FAFC' : '#94A3B8',
                  backgroundColor: isActive('/app') || isActive('/app/') ? 'rgba(59, 130, 246, 0.1)' : 'transparent'
                }}
                data-testid="nav-dashboard"
              >
                Dashboard
              </Link>

              {/* Auth Section */}
              {isAuthenticated ? (
                <div className="flex items-center gap-2 ml-2">
                  {/* Notifications Bell */}
                  {isPro ? (
                    <NotificationBell 
                      onClick={() => setShowNotifications(!showNotifications)}
                      isPro={isPro}
                    />
                  ) : (
                    <button
                      onClick={() => { setUpgradeFeature('Notifications'); setShowUpgradeModal(true); }}
                      className="relative p-2 rounded-lg transition-colors"
                      style={{ color: '#64748B' }}
                      title="Upgrade to Pro for notifications"
                    >
                      <Bell className="w-5 h-5" />
                      <Lock className="absolute -bottom-0.5 -right-0.5 w-3 h-3" />
                    </button>
                  )}
                  
                  {/* Gift Icon - Referral */}
                  <button
                    onClick={() => setShowReferralModal(true)}
                    className="p-2 rounded-lg transition-colors hover:bg-white/5"
                    title="Refer & Earn"
                    data-testid="gift-btn"
                  >
                    <Gift className="w-5 h-5" style={{ color: '#94A3B8' }} />
                  </button>

                  {/* User Avatar Dropdown */}
                  <div className="relative">
                    <button
                      ref={menuTriggerRef}
                      onClick={() => {
                        setShowUserMenu(!showUserMenu);
                        setShowLanguageMenu(false);
                      }}
                      className="flex items-center gap-2 px-3 py-2 rounded-lg transition-colors"
                      style={{
                        backgroundColor: showUserMenu ? 'rgba(255,255,255,0.05)' : 'transparent'
                      }}
                      data-testid="user-menu-btn"
                    >
                      <div 
                        className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium"
                        style={{ 
                          background: isPremium 
                            ? 'linear-gradient(135deg, #3B82F6 0%, #8B5CF6 100%)' 
                            : 'rgba(148, 163, 184, 0.2)',
                          color: '#F8FAFC'
                        }}
                      >
                        {getUserInitial()}
                      </div>
                      {isPremium && (
                        <Crown className="w-4 h-4" style={{ color: '#3B82F6' }} />
                      )}
                      <ChevronDown className="w-4 h-4" style={{ color: '#64748B' }} />
                    </button>

                    {/* Dropdown Menu - Rendered via Portal */}
                    {showUserMenu && createPortal(
                      <div 
                        ref={menuRef}
                        className="fixed w-56 rounded-xl py-2"
                        style={{
                          top: `${menuPosition.top}px`,
                          left: `${menuPosition.left}px`,
                          background: '#0E1324',
                          border: '1px solid rgba(59, 130, 246, 0.15)',
                          boxShadow: '0 10px 40px rgba(0,0,0,0.5)',
                          zIndex: 9999,
                          maxHeight: '70vh',
                          overflowY: 'auto'
                        }}
                        data-testid="user-dropdown"
                      >
                        {/* User Info */}
                        <div className="px-4 py-2 border-b" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
                          <p className="text-sm font-medium truncate" style={{ color: '#F8FAFC' }}>
                            {user?.email}
                          </p>
                          <p className="text-xs mt-0.5" style={{ color: isPremium ? '#3B82F6' : '#64748B' }}>
                            {isPremium ? 'Pro Plan' : 'Free Plan'}
                          </p>
                        </div>

                        {/* Menu Items */}
                        <div className="py-1">
                          {/* === SECTION 1: User Features === */}
                          
                          {/* Account Settings */}
                          <button
                            onClick={() => {
                              setShowAccountModal(true);
                              setShowUserMenu(false);
                            }}
                            className="flex items-center gap-3 px-4 py-2 text-sm transition-colors w-full text-left"
                            style={{ color: '#94A3B8' }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.05)'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                            data-testid="account-settings-btn"
                          >
                            <User className="w-4 h-4" />
                            Account settings
                          </button>

                          {/* Watchlist - Pro Feature */}
                          {isPro ? (
                            <Link
                              to="/app/watchlist"
                              onClick={() => setShowUserMenu(false)}
                              className="flex items-center gap-3 px-4 py-2 text-sm transition-colors"
                              style={{ 
                                color: isActive('/app/watchlist') ? '#F8FAFC' : '#94A3B8',
                                backgroundColor: isActive('/app/watchlist') ? 'rgba(59, 130, 246, 0.1)' : 'transparent'
                              }}
                              onMouseEnter={(e) => { if (!isActive('/app/watchlist')) e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.05)'; }}
                              onMouseLeave={(e) => { if (!isActive('/app/watchlist')) e.currentTarget.style.backgroundColor = 'transparent'; }}
                              data-testid="dropdown-watchlist"
                            >
                              <Star className="w-4 h-4" />
                              Watchlist
                            </Link>
                          ) : (
                            <button
                              onClick={() => { setUpgradeFeature('Watchlists'); setShowUpgradeModal(true); setShowUserMenu(false); }}
                              className="flex items-center justify-between gap-3 px-4 py-2 text-sm transition-colors w-full text-left"
                              style={{ color: '#64748B' }}
                              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.05)'}
                              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                              data-testid="dropdown-watchlist-locked"
                            >
                              <div className="flex items-center gap-3">
                                <Star className="w-4 h-4" />
                                Watchlist
                              </div>
                              <Lock className="w-3 h-3" />
                            </button>
                          )}

                          {/* Alerts - Pro Feature */}
                          {isPro ? (
                            <Link
                              to="/app/alerts"
                              onClick={() => setShowUserMenu(false)}
                              className="flex items-center gap-3 px-4 py-2 text-sm transition-colors"
                              style={{ 
                                color: isActive('/app/alerts') ? '#F8FAFC' : '#94A3B8',
                                backgroundColor: isActive('/app/alerts') ? 'rgba(59, 130, 246, 0.1)' : 'transparent'
                              }}
                              onMouseEnter={(e) => { if (!isActive('/app/alerts')) e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.05)'; }}
                              onMouseLeave={(e) => { if (!isActive('/app/alerts')) e.currentTarget.style.backgroundColor = 'transparent'; }}
                              data-testid="dropdown-alerts"
                            >
                              <AlertCircle className="w-4 h-4" />
                              Alerts
                            </Link>
                          ) : (
                            <button
                              onClick={() => { setUpgradeFeature('Alerts'); setShowUpgradeModal(true); setShowUserMenu(false); }}
                              className="flex items-center justify-between gap-3 px-4 py-2 text-sm transition-colors w-full text-left"
                              style={{ color: '#64748B' }}
                              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.05)'}
                              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                              data-testid="dropdown-alerts-locked"
                            >
                              <div className="flex items-center gap-3">
                                <AlertCircle className="w-4 h-4" />
                                Alerts
                              </div>
                              <Lock className="w-3 h-3" />
                            </button>
                          )}

                          {/* Settings */}
                          <Link
                            to="/app/config"
                            onClick={() => setShowUserMenu(false)}
                            className="flex items-center gap-3 px-4 py-2 text-sm transition-colors"
                            style={{ 
                              color: isActive('/app/config') ? '#F8FAFC' : '#94A3B8',
                              backgroundColor: isActive('/app/config') ? 'rgba(59, 130, 246, 0.1)' : 'transparent'
                            }}
                            onMouseEnter={(e) => { if (!isActive('/app/config')) e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.05)'; }}
                            onMouseLeave={(e) => { if (!isActive('/app/config')) e.currentTarget.style.backgroundColor = 'transparent'; }}
                            data-testid="dropdown-settings"
                          >
                            <Settings className="w-4 h-4" />
                            Settings
                          </Link>

                          {/* === Separator === */}
                          <div className="my-1 h-px" style={{ backgroundColor: 'rgba(255,255,255,0.06)' }} />

                          {/* === SECTION 2: General === */}
                          
                          {/* Support Tickets */}
                          <Link
                            to="/app/tickets"
                            onClick={() => setShowUserMenu(false)}
                            className="flex items-center gap-3 px-4 py-2 text-sm transition-colors"
                            style={{ color: '#94A3B8' }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.05)'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                            data-testid="support-tickets-btn"
                          >
                            <MessageSquare className="w-4 h-4" />
                            Support
                          </Link>

                          {/* Language Submenu Trigger */}
                          <div className="relative">
                            <button
                              ref={languageTriggerRef}
                              onClick={() => setShowLanguageMenu(!showLanguageMenu)}
                              className="flex items-center justify-between gap-3 px-4 py-2 text-sm transition-colors w-full text-left"
                              style={{ color: '#94A3B8' }}
                              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.05)'}
                              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = showLanguageMenu ? 'rgba(255,255,255,0.05)' : 'transparent'}
                              data-testid="language-submenu-btn"
                            >
                              <div className="flex items-center gap-3">
                                <Globe className="w-4 h-4" />
                                Language
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-xs">{getCurrentLanguage().flag}</span>
                                <ChevronRight className="w-4 h-4" style={{ transform: languageMenuSide === 'left' ? 'rotate(180deg)' : 'none' }} />
                              </div>
                            </button>
                          </div>

                          {/* Start Tutorial */}
                          <button
                            onClick={() => {
                              setShowUserMenu(false);
                              // Start the appropriate tour based on current location
                              if (location.pathname.includes('/stock/')) {
                                startTour(TOUR_IDS.STOCK_DETAIL);
                              } else {
                                startTour(TOUR_IDS.DASHBOARD);
                              }
                            }}
                            className="flex items-center gap-3 px-4 py-2 text-sm transition-colors w-full text-left"
                            style={{ color: '#94A3B8' }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.05)'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                            data-testid="start-tutorial-btn"
                          >
                            <HelpCircle className="w-4 h-4" />
                            Start Tutorial
                          </button>

                          {/* === Separator === */}
                          <div className="my-1 h-px" style={{ backgroundColor: 'rgba(255,255,255,0.06)' }} />

                          {/* === SECTION 3: Billing === */}
                          
                          {/* Manage Subscription */}
                          <Link
                            to="/app/billing"
                            onClick={() => setShowUserMenu(false)}
                            className="flex items-center gap-3 px-4 py-2 text-sm transition-colors"
                            style={{ color: '#94A3B8' }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.05)'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                            data-testid="billing-btn"
                          >
                            <Crown className="w-4 h-4" />
                            {isPremium ? 'Manage Subscription' : 'Upgrade to Pro'}
                          </Link>

                          {/* === Separator === */}
                          <div className="my-1 h-px" style={{ backgroundColor: 'rgba(255,255,255,0.06)' }} />

                          {/* === SECTION 4: Sign Out === */}
                          
                          {/* Logout */}
                          <button
                            onClick={handleLogout}
                            className="flex items-center gap-3 px-4 py-2 text-sm transition-colors w-full text-left"
                            style={{ color: '#f87171' }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(248,113,113,0.1)'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                            data-testid="logout-btn"
                          >
                            <LogOut className="w-4 h-4" />
                            Sign out
                          </button>
                        </div>
                      </div>,
                      document.body
                    )}

                    {/* Language Submenu - Rendered via Portal */}
                    {showLanguageMenu && createPortal(
                      <div 
                        ref={languageMenuRef}
                        className="fixed w-44 rounded-xl py-2"
                        style={{
                          top: `${languageMenuPosition.top}px`,
                          left: `${languageMenuPosition.left}px`,
                          background: '#0E1324',
                          border: '1px solid rgba(59, 130, 246, 0.15)',
                          boxShadow: '0 10px 40px rgba(0,0,0,0.5)',
                          zIndex: 10000,
                          maxHeight: '70vh',
                          overflowY: 'auto'
                        }}
                        data-testid="language-submenu"
                      >
                        {LANGUAGES.map((lang) => (
                          <button
                            key={lang.code}
                            onClick={() => handleLanguageSelect(lang.code)}
                            className="flex items-center justify-between gap-3 px-4 py-2 text-sm transition-colors w-full text-left"
                            style={{ 
                              color: user?.language === lang.code ? '#F8FAFC' : '#94A3B8',
                              backgroundColor: user?.language === lang.code ? 'rgba(59, 130, 246, 0.1)' : 'transparent'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.05)'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = user?.language === lang.code ? 'rgba(59, 130, 246, 0.1)' : 'transparent'}
                            data-testid={`lang-option-${lang.code}`}
                          >
                            <div className="flex items-center gap-2">
                              <span>{lang.flag}</span>
                              <span>{lang.name}</span>
                            </div>
                            {user?.language === lang.code && (
                              <Check className="w-4 h-4" style={{ color: '#3B82F6' }} />
                            )}
                          </button>
                        ))}
                      </div>,
                      document.body
                    )}
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-2 ml-2">
                  <Link
                    to="/login"
                    className="px-4 py-2 text-sm font-medium transition-colors rounded-lg"
                    style={{ color: '#94A3B8' }}
                    data-testid="nav-login"
                  >
                    Sign in
                  </Link>
                  <Link
                    to="/signup"
                    className="px-4 py-2 text-sm font-medium rounded-lg transition-colors"
                    style={{
                      background: '#3B82F6',
                      color: 'white'
                    }}
                    data-testid="nav-signup"
                  >
                    Get Started
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Modals */}
      <AccountSettingsModal 
        isOpen={showAccountModal} 
        onClose={() => setShowAccountModal(false)} 
      />
      <ReferralModal 
        isOpen={showReferralModal} 
        onClose={() => setShowReferralModal(false)} 
      />
      <UpgradeModal 
        isOpen={showUpgradeModal} 
        onClose={() => setShowUpgradeModal(false)}
        feature={upgradeFeature}
      />
      <NotificationsDropdown 
        isOpen={showNotifications}
        onClose={() => setShowNotifications(false)}
        onUpgradeClick={() => { setShowNotifications(false); setUpgradeFeature('Notifications'); setShowUpgradeModal(true); }}
      />
    </>
  );
};

export default Navigation;
